<template>
  <div>
    <div v-if="active" class="overlay" @click="onSelectionClick" />

    <div class="relative">
      <button
        class="color-toggle"
        :class="{ loading: loading }"
        :disabled="loading"
        @click="onSelectionClick"
      >
        <div v-if="loading" class="loading-icon-wrap">
          <span class="loading-label">
            Saving
          </span>
          <v-icon name="spinner" title="Loading" class="loading-icon" spin />
        </div>

        <div v-if="!loading" class="flex items-center">
          <div class="flex-1">
            <span class="color-preview-wrap">
              <span class="color-preview" :style="previewColor" />
            </span>
          </div>
          <div class="flex-1 pl-2">
            <v-icon v-if="!active" name="chevron-down" class="icon" />
            <v-icon v-if="active" name="chevron-up" class="icon" />
          </div>
        </div>
      </button>
      <SketchPicker
        v-if="active"
        :value="currentColor"
        class="color-picker"
        :preset-colors="brandColors"
        @input="onUpdateValue"
      />
      <slot name="label">
        <label v-if="label" :for="name" class="label">
          {{ label }}
        </label>
      </slot>
    </div>
  </div>
</template>

<script>
import { Sketch as SketchPicker } from "vue-color";
import { Brand } from "@/graphql/queries";

export default {
  apollo: {
    $query: {
      fetchPolicy: "cache-first",
    },
    brand: {
      skip() {
        // only run this when the user opens the color picker
        return !this.active;
      },
      query: () => Brand,
      update: (data) => data.brand,
    },
  },
  name: "ColorPicker",
  components: { SketchPicker },
  props: {
    label: {
      default: "",
      type: String,
    },
    loading: Boolean,
    name: {
      default: "",
      type: String,
    },
    color: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      active: false,
      brand: {},
      colorUpdated: false,
      currentColor: this.color,
    };
  },
  computed: {
    brandColors() {
      const colors = [];
      const { primaryColor, secondaryColor } = this.brand;

      if (primaryColor) colors.push(primaryColor);
      // Only set the secondaryColor if it's not the same as the primaryColor
      if (secondaryColor && secondaryColor !== primaryColor)
        colors.push(secondaryColor);

      return colors;
    },
    previewColor() {
      return `background-color: ${this.currentColor};`;
    },
  },
  methods: {
    emitColorUpdate() {
      // If the color has been updated...
      if (this.colorUpdated) {
        // Emit the color selected event...
        this.$emit("color-selected", {
          item: this.name,
          value: this.currentColor,
        });

        // And set the color updated prop back to false...
        this.colorUpdated = false;
      }
    },
    onSelectionClick() {
      if (this.active) this.emitColorUpdate();

      this.active = !this.active;
    },
    onUpdateValue(color) {
      // Set whether the color has been updated
      if (this.currentColor !== color.hex) this.colorUpdated = true;

      // Update to the new color
      this.currentColor = color.hex;
    },
  },
};
</script>

<style scoped>
.overlay {
  @apply bg-transparent h-full w-full fixed;
  left: 0;
  top: 0;
  z-index: 1;
}

.label {
  @apply text-support;
}

.color-toggle {
  @apply border border-grey-light rounded-lg bg-white p-2 outline-none shadow;
  width: 76px;
}

.color-preview-wrap {
  @apply block border border-grey-light rounded-lg bg-white;
  padding: 5px;
}

.color-preview {
  @apply block rounded;
  height: 20px;
  width: 20px;
}

.color-picker {
  @apply absolute;
  top: -215px;
  z-index: 1000;
}

.icon {
  @apply block text-grey;
}

.loading-icon-wrap {
  @apply text-brand;
  line-height: 1.4;
  height: 31px;
  width: 55px;
}

.loading-icon {
  font-size: 12px;
  position: relative;
  top: 2px;
}

.loading-label {
  font-size: 12px;
  margin-right: 2px;
}

/* Chrome picker syles */
>>> .vc-sketch {
  @apply rounded-lg overflow-hidden;
}

>>> .vc-editable-input {
  .vc-input__input {
    @apply rounded text-center px-0 w-full;
  }

  .vc-input__label {
    @apply mt-0;
  }
}
</style>
